/**
 * Route Metadata Registry — Single source of truth for page headers.
 *
 * Every route in the app gets its title, breadcrumbs, and zone/group
 * assignment from THIS registry. The AppLayout reads this automatically
 * to render the PageHeader — individual pages do NOT need to import
 * or render PageHeader themselves.
 *
 * To add a new page:
 *   1. Create the route component
 *   2. Add the route to router.tsx
 *   3. Add ONE line here → done. Header + breadcrumbs just work.
 *
 * v3.0 — Reorganised breadcrumbs to match v4 sidebar IA.
 *
 * @since 2.1.0
 */

export interface RouteMeta {
    /** Page title (rendered in serif font) */
    title: string;
    /** Optional subtitle shown below title */
    subtitle?: string;
    /** Breadcrumb trail — auto-prepends "Home" */
    breadcrumbs: { label: string; href?: string }[];
    /** If true, skip the auto-header (for pages like Dashboard that have custom headers) */
    noHeader?: boolean;
    /**
     * Draw the header in its compact one-line bar from the start (~32px, no
     * serif title, no subtitle) instead of the full ~80px block.
     *
     * For a screen whose own first card already says what the screen is and
     * which thing it is about — there the tall header repeats it and charges
     * the reader a third of the fold for the privilege. Not a default: most
     * pages genuinely are introduced by their title.
     */
    dense?: boolean;
}

/**
 * Master registry: path → metadata.
 *
 * Convention:
 *   - Breadcrumbs are listed WITHOUT "Home" — that's prepended automatically
 *   - The last breadcrumb is the current page (no href)
 *   - First breadcrumb matches the sidebar zone/group for orientation
 */
const ROUTE_REGISTRY: Record<string, RouteMeta> = {

    // ═══════════════════════════════════════════════════════════
    //  DASHBOARD
    // ═══════════════════════════════════════════════════════════
    '/': {
        title: 'Dashboard',
        breadcrumbs: [{ label: 'Dashboard' }],
        noHeader: true,
    },
    '/dashboard': {
        title: 'Dashboard',
        breadcrumbs: [{ label: 'Dashboard' }],
        // The layout reads the address, and both homes live here: the Flow home
        // has no header, the Books dashboard draws its own (plainface-b).
        noHeader: true,
    },
    '/modules': {
        title: 'Modules',
        subtitle: 'Turn Karr features on or off for your organization',
        breadcrumbs: [{ label: 'Modules' }],
    },

    // ═══════════════════════════════════════════════════════════
    //  SALES
    // ═══════════════════════════════════════════════════════════
    '/sales': {
        title: 'Sales & Invoices',
        subtitle: 'Manage invoices, payments, and revenue',
        breadcrumbs: [
            { label: 'Sales', href: '/sales' },
            { label: 'Invoices' },
        ],
    },
    '/estimates': {
        title: 'Estimates & Quotations',
        subtitle: 'Create and manage quotations',
        breadcrumbs: [
            { label: 'Sales', href: '/sales' },
            { label: 'Estimates' },
        ],
    },
    '/credit-notes': {
        title: 'Credit Notes',
        subtitle: 'Sales returns and credit adjustments',
        breadcrumbs: [
            { label: 'Sales', href: '/sales' },
            { label: 'Credit Notes' },
        ],
    },
    '/delivery-challans': {
        title: 'Delivery Challans',
        subtitle: 'Track goods dispatched',
        breadcrumbs: [
            { label: 'Sales', href: '/sales' },
            { label: 'Delivery Challans' },
        ],
    },
    '/recurring-invoices': {
        title: 'Recurring Invoices',
        subtitle: 'Automatically generated invoices on a schedule',
        breadcrumbs: [
            { label: 'Sales', href: '/sales' },
            { label: 'Recurring Invoices' },
        ],
    },
    '/payment-links': {
        title: 'Payment Links',
        subtitle: 'Create and share payment links',
        breadcrumbs: [
            { label: 'Sales', href: '/sales' },
            { label: 'Payment Links' },
        ],
    },

    // ═══════════════════════════════════════════════════════════
    //  PURCHASES
    // ═══════════════════════════════════════════════════════════
    '/purchases': {
        title: 'Purchases & Bills',
        subtitle: 'Manage bills and vendor payments',
        breadcrumbs: [
            { label: 'Money Out', href: '/money-out' },
            { label: 'Bills' },
        ],
    },
    '/purchase-orders': {
        title: 'Purchase Orders',
        subtitle: 'Track orders placed with vendors',
        breadcrumbs: [
            { label: 'Money Out', href: '/money-out' },
            { label: 'Purchase Orders' },
        ],
    },
    '/debit-notes': {
        title: 'Debit Notes',
        subtitle: 'Purchase returns and debit adjustments',
        breadcrumbs: [
            { label: 'Money Out', href: '/money-out' },
            { label: 'Debit Notes' },
        ],
    },
    '/goods-received': {
        title: 'Goods Received Notes',
        subtitle: 'Record goods received against orders',
        breadcrumbs: [
            { label: 'Money Out', href: '/money-out' },
            { label: 'Goods Received' },
        ],
    },
    '/fixed-expenses': {
        title: 'Fixed expenses',
        subtitle: 'Rent, electricity, the audit fee: what you pay every month or every year, and about how much',
        breadcrumbs: [
            { label: 'Books' },
            { label: 'Fixed expenses' },
        ],
    },
    '/recurring-bills': {
        title: 'Supplier bills that repeat',
        subtitle: 'Automatically generated bills on a schedule',
        breadcrumbs: [
            { label: 'Money Out', href: '/money-out' },
            { label: 'Recurring Bills' },
        ],
    },
    '/expenses': {
        title: 'Expenses',
        subtitle: 'Track and categorise business expenses',
        breadcrumbs: [
            { label: 'Money Out', href: '/money-out' },
            { label: 'Expenses' },
        ],
    },

    // ═══════════════════════════════════════════════════════════
    //  BANKING
    // ═══════════════════════════════════════════════════════════
    '/money-in': {
        title: 'Money In',
        subtitle: 'Record payments received',
        breadcrumbs: [
            { label: 'Banking', href: '/money-in' },
            { label: 'Money In' },
        ],
    },
    '/money-out': {
        title: 'Money Out',
        subtitle: 'Record payments made',
        breadcrumbs: [
            { label: 'Banking', href: '/money-in' },
            { label: 'Money Out' },
        ],
    },
    '/transfers': {
        title: 'Transfers',
        subtitle: 'Inter-account fund transfers',
        breadcrumbs: [
            { label: 'Banking', href: '/money-in' },
            { label: 'Transfers' },
        ],
    },
    '/bank-statements': {
        title: 'Bank Statements',
        subtitle: 'Upload each statement; Karr checks it and uses it everywhere',
        breadcrumbs: [
            { label: 'Banking', href: '/money-in' },
            { label: 'Bank Statements' },
        ],
    },
    '/bank-reconciliation': {
        title: 'Bank Reconciliation',
        subtitle: 'Match transactions with bank statements',
        breadcrumbs: [
            { label: 'Banking', href: '/money-in' },
            { label: 'Reconciliation' },
        ],
    },
    '/bank-feeds': {
        title: 'Bank Feeds',
        subtitle: 'Auto-import transactions from your bank',
        breadcrumbs: [
            { label: 'Banking', href: '/money-in' },
            { label: 'Bank Feeds' },
        ],
    },

    // ═══════════════════════════════════════════════════════════
    //  CONTACTS & ITEMS
    // ═══════════════════════════════════════════════════════════
    '/contacts': {
        title: 'Contacts',
        subtitle: 'Customers, vendors, and other contacts',
        breadcrumbs: [{ label: 'Contacts' }],
    },
    '/product-codes': {
        title: 'Product codes',
        subtitle: 'Every item whose HSN/SAC the portal would refuse, and the code Karr found',
        breadcrumbs: [{ label: 'Product codes' }],
    },
    '/items': {
        title: 'Items & Services',
        subtitle: 'Products and services you buy or sell',
        breadcrumbs: [{ label: 'Items & Services' }],
    },

    // ═══════════════════════════════════════════════════════════
    //  ACCOUNTING
    // ═══════════════════════════════════════════════════════════
    '/journals': {
        title: 'Journal Entries',
        subtitle: 'Manual accounting entries',
        breadcrumbs: [
            { label: 'Accounting', href: '/accounts' },
            { label: 'Journal Entries' },
        ],
    },
    '/accounts': {
        title: 'Chart of Accounts',
        subtitle: 'Manage your ledger accounts',
        breadcrumbs: [
            { label: 'Accounting', href: '/accounts' },
            { label: 'Chart of Accounts' },
        ],
    },
    '/multi-currency': {
        title: 'Multi-Currency',
        subtitle: 'Manage foreign currency transactions',
        breadcrumbs: [
            { label: 'Accounting', href: '/accounts' },
            { label: 'Multi-Currency' },
        ],
    },
    '/consolidation': {
        title: 'Consolidation',
        subtitle: 'Consolidated financial statements across entities',
        breadcrumbs: [
            { label: 'Accounting', href: '/accounts' },
            { label: 'Consolidation' },
        ],
    },

    // ─── Assets & Liabilities ────────────────────────────────
    '/fixed-assets': {
        title: 'Fixed Assets',
        subtitle: 'Track and depreciate capital assets',
        breadcrumbs: [
            { label: 'Assets & Liabilities' },
            { label: 'Fixed Assets' },
        ],
    },
    '/loans': {
        title: 'Loans',
        subtitle: 'Track loans, EMIs, and interest',
        breadcrumbs: [
            { label: 'Assets & Liabilities' },
            { label: 'Loans' },
        ],
    },

    // ═══════════════════════════════════════════════════════════
    //  TAX & COMPLIANCE
    // ═══════════════════════════════════════════════════════════
    '/compliance-reports': {
        title: 'Compliance Reports',
        subtitle: 'Regulatory filings and compliance status',
        breadcrumbs: [
            // indiacockpit: every country has this page; only India has the GST cockpit.
            { label: 'Tax & Compliance', href: '/compliance' },
            { label: 'Compliance Reports' },
        ],
    },
    '/pay-week': {
        title: 'Payments Due',
        subtitle: 'Everything leaving your bank soon — ranked by urgency',
        breadcrumbs: [
            { label: 'Banking', href: '/money-out' },
            { label: 'Payments Due' },
        ],
    },
    '/compliance': {
        title: 'Compliance Intelligence',
        subtitle: 'Live risk, rules & filings — caught before they cost you',
        breadcrumbs: [
            { label: 'Tax & Compliance', href: '/gst-filing' },
            { label: 'Intelligence' },
        ],
    },
    '/notices': {
        title: 'Notices & demands',
        subtitle: 'Everything that arrives after a return is filed — and the windows that close on it',
        breadcrumbs: [
            { label: 'Tax & Compliance', href: '/gst-filing' },
            { label: 'Notices & demands' },
        ],
    },
    // noticeproof np-9: the page draws its own header (the year is in its title).
    '/ties': {
        title: 'The year side by side',
        subtitle: 'GST, income tax and ROC figures, compared the way the department compares them',
        breadcrumbs: [
            { label: 'Tax & Compliance', href: '/gst-filing' },
            { label: 'The year side by side' },
        ],
    },
    '/books-health': {
        title: 'Books Health',
        subtitle: 'Karr\'s robot accountant checks your double-entry books every night',
        breadcrumbs: [
            { label: 'Tax & Compliance', href: '/gst-filing' },
            { label: 'Books Health' },
        ],
    },
    // hf-4: the hospital's people directory (owners, admins, HR). It draws its own header.
    '/health/people': {
        title: 'People',
        subtitle: 'The hospital\'s staff: roles, departments, licences and logins',
        breadcrumbs: [
            { label: 'Karr Health', href: '/health' },
            { label: 'People' },
        ],
        noHeader: true,
    },
    // hf-15: the duty roster on Karr Care's tables. It draws its own header.
    '/health/roster': {
        title: 'Duty roster',
        subtitle: 'Who is on which ward and shift, and the licences to look at',
        breadcrumbs: [
            { label: 'Karr Health', href: '/health' },
            { label: 'Duty roster' },
        ],
        noHeader: true,
    },
    '/admin/books-health': {
        title: 'Books Health — Fleet',
        subtitle: 'Latest nightly run per org',
        breadcrumbs: [
            { label: 'Admin', href: '/admin' },
            { label: 'Books Health' },
        ],
    },
    '/statutory-registers': {
        title: 'Statutory Registers',
        subtitle: 'Who the people of this business are, on the record: members, directors and KMP for a company, owners for everybody else, and the signature each of them has given',
        breadcrumbs: [
            { label: 'Tax & Compliance', href: '/gst-filing' },
            { label: 'Statutory Registers' },
        ],
    },
    '/roc': {
        title: 'ROC Compliance',
        subtitle: 'Your Companies Act year, as a map — every filing, meeting and paper, on time',
        breadcrumbs: [
            { label: 'Tax & Compliance', href: '/gst-filing' },
            { label: 'ROC Compliance' },
        ],
    },
    // auditflow A4: the 3CD lives inside Audit now; advance tax is the
    // client's own income-tax planning and has its own door.
    '/advance-tax': {
        title: 'Advance tax',
        subtitle: 'The instalments due under section 211 and the interest at risk under sections 234B and 234C, off the live books',
        breadcrumbs: [
            { label: 'Tax & Compliance', href: '/itr-filing' },
            { label: 'Advance tax' },
        ],
    },

    // ═══════════════════════════════════════════════════════════
    //  KARR LAB (LIS vertical)
    // ═══════════════════════════════════════════════════════════
    '/lab': {
        title: 'Karr Lab',
        subtitle: 'Orders, samples & reports — the lab runs here, your books stay in sync',
        breadcrumbs: [
            { label: 'Karr Lab' },
        ],
    },

    // ═══════════════════════════════════════════════════════════
    //  REPORTS & ANALYSIS
    // ═══════════════════════════════════════════════════════════
    '/reports': {
        title: 'Reports',
        subtitle: 'Financial statements and analytics',
        breadcrumbs: [{ label: 'Reports & Analysis' }],
    },
    // me-1: Karr Me, the person's own year (docs/KARR_ME_SPEC.md).
    '/me': {
        title: 'Karr Me',
        subtitle: 'Your own year, ready to sign on 1 April',
        breadcrumbs: [{ label: 'Karr Me' }],
    },
    '/projections': {
        title: 'Projections & Bank Finance',
        subtitle: 'Bank-ready projected financial statements from your live books',
        breadcrumbs: [
            { label: 'Reports & Analysis', href: '/reports' },
            { label: 'Projections & Bank Finance' },
        ],
    },
    // fleet-1: Karr Fleet's first desk, vehicles and drivers (docs/KARR_FLEET_SPEC.md).
    '/fleet': {
        title: 'Karr Fleet',
        subtitle: 'Your vehicles and drivers, on the books',
        breadcrumbs: [
            { label: 'Karr Fleet' },
        ],
    },
    // mandi-1: Karr Mandi's first desk, the Gate: today's lots (docs/KARR_MANDI_SPEC.md).
    '/mandi': {
        title: 'Karr Mandi',
        subtitle: 'The lots weighed at the gate today',
        breadcrumbs: [
            { label: 'Karr Mandi' },
        ],
    },
    // stay-1: Karr Stay's first desk, rooms, room types and rates (docs/KARR_STAY_SPEC.md).
    '/stay': {
        title: 'Karr Stay',
        subtitle: 'Your rooms, priced by the night',
        breadcrumbs: [
            { label: 'Karr Stay' },
        ],
    },
    // campus-1: Karr Campus's first desk, classes, the roll and the terms (docs/KARR_SCHOOL_SPEC.md).
    // campus-2..5: the term's fees, raised as invoices, taken, and given back as concessions.
    '/campus': {
        title: 'Karr Campus',
        subtitle: 'The term\'s fees, your classes and their students',
        breadcrumbs: [
            { label: 'Karr Campus' },
        ],
    },
    // jewel-1: Karr Jewel's first desk, today's rate and the metals (docs/KARR_JEWEL_SPEC.md).
    '/jewel': {
        title: 'Karr Jewel',
        subtitle: "Today's rate and the metal in stock",
        breadcrumbs: [
            { label: 'Karr Jewel' },
        ],
    },
    // studio-1: Karr Studio's seven desks (docs/KARR_STUDIO_SPEC.md §3).
    '/studio': {
        title: 'Karr Studio · Book',
        subtitle: 'The day by chair, room or floor',
        breadcrumbs: [
            { label: 'Karr Studio' },
        ],
    },
    '/studio/classes': {
        title: 'Karr Studio · Classes',
        subtitle: 'Classes of the week',
        breadcrumbs: [
            { label: 'Karr Studio', href: '/studio' },
            { label: 'Classes' },
        ],
    },
    '/studio/clients': {
        title: 'Karr Studio · Clients',
        subtitle: 'Your clients and members',
        breadcrumbs: [
            { label: 'Karr Studio', href: '/studio' },
            { label: 'Clients' },
        ],
    },
    '/studio/till': {
        title: 'Karr Studio · Till',
        subtitle: 'Bills on Karr POS',
        breadcrumbs: [
            { label: 'Karr Studio', href: '/studio' },
            { label: 'Till' },
        ],
    },
    '/studio/staff': {
        title: 'Karr Studio · Staff',
        subtitle: 'Your team on the chairs and the floor',
        breadcrumbs: [
            { label: 'Karr Studio', href: '/studio' },
            { label: 'Staff' },
        ],
    },
    '/studio/morning': {
        title: 'Karr Studio · Morning',
        subtitle: 'Figures for the owner',
        breadcrumbs: [
            { label: 'Karr Studio', href: '/studio' },
            { label: 'Morning' },
        ],
    },
    '/studio/page': {
        title: 'Karr Studio · Page',
        subtitle: 'Your own booking page',
        breadcrumbs: [
            { label: 'Karr Studio', href: '/studio' },
            { label: 'Page' },
        ],
    },
    // service-1: Karr Service's first desk, customer machines and their contracts (docs/KARR_SERVICE_SPEC.md).
    '/service': {
        title: 'Karr Service',
        subtitle: 'Your customers\' machines and the contracts that cover them',
        breadcrumbs: [
            { label: 'Karr Service' },
        ],
    },
    '/report-builder': {
        title: 'Report Builder',
        subtitle: 'Create custom reports',
        breadcrumbs: [
            { label: 'Reports & Analysis', href: '/reports' },
            { label: 'Report Builder' },
        ],
    },
    '/receivables': {
        title: 'Receivables & Payables',
        subtitle: 'Outstanding amounts and ageing',
        breadcrumbs: [
            { label: 'Reports & Analysis', href: '/reports' },
            { label: 'Receivables & Payables' },
        ],
    },
    '/budgeting': {
        title: 'Budgeting & Forecasts',
        subtitle: 'Set and monitor financial budgets',
        breadcrumbs: [
            { label: 'Reports & Analysis', href: '/reports' },
            { label: 'Budgeting & Forecasts' },
        ],
    },
    '/ngo-reports': {
        title: 'NGO Reports',
        subtitle: 'Receipts & payments, fund utilisation and FCRA',
        breadcrumbs: [
            { label: 'Reports & Analysis', href: '/reports' },
            { label: 'NGO Reports' },
        ],
    },
    '/client-sheet': {
        title: 'Client sheet',
        subtitle: 'Every number and portal login of a client, each with a copy button',
        breadcrumbs: [
            { label: 'Client sheet' },
        ],
    },
    '/office-credentials': {
        noHeader: true, // dscgrid: like every office tab, the PracticeTabs strip carries the identity
        title: 'Client logins',
        subtitle: "Every client's portal logins in one CSV: download, fill in, upload",
        breadcrumbs: [
            { label: 'Office', href: '/portfolio' },
            { label: 'Client logins' },
        ],
    },
    '/practice/matters': {
        noHeader: true, // practice-1: an Office tab, so the PracticeTabs strip carries the identity
        title: 'Matters',
        subtitle: 'Every open matter or project: its client, its next date, its hours and what is still to bill',
        breadcrumbs: [
            { label: 'Office', href: '/portfolio' },
            { label: 'Matters' },
        ],
    },
    // practice-12: where a share to Karr lands, for a moment, on its way to where it belongs.
    '/share': {
        title: 'Shared to Karr',
        subtitle: 'A receipt goes to the scan; a court page or a court order to its matter',
        breadcrumbs: [{ label: 'Shared to Karr' }],
    },
    '/office-dsc': {
        noHeader: true, // dscgrid: like every office tab, the PracticeTabs strip carries the identity
        title: 'DSCs',
        subtitle: "Every client's DSCs, where each token is, and which renewals to start",
        breadcrumbs: [
            { label: 'Office', href: '/portfolio' },
            { label: 'DSCs' },
        ],
    },
    '/office-tds': {
        noHeader: true, // officetds: like every office tab, the PracticeTabs strip carries the identity
        title: 'TDS',
        subtitle: "Every client's TDS quarter, one row per client and form",
        breadcrumbs: [
            { label: 'Office', href: '/portfolio' },
            { label: 'TDS' },
        ],
    },
    '/office-filings': {
        noHeader: true, // like every office tab, the PracticeTabs strip carries the identity
        title: 'Filings',
        subtitle: "What every client's filings still need, one row per item",
        breadcrumbs: [
            { label: 'Office', href: '/portfolio' },
            { label: 'Filings' },
        ],
    },
    '/donations': {
        title: 'Donations & receipts',
        subtitle: 'Record a donation, email the donor their receipt, keep the section 354 approval alive',
        breadcrumbs: [
            { label: 'Reports & Analysis', href: '/reports' },
            { label: 'Donations & receipts' },
        ],
    },

    // ═══════════════════════════════════════════════════════════
    //  MODULES
    // ═══════════════════════════════════════════════════════════
    '/inventory': {
        title: 'Inventory',
        subtitle: 'Stock levels, warehouses, and movements',
        breadcrumbs: [
            { label: 'Modules' },
            { label: 'Inventory' },
        ],
    },
    '/payroll': {
        title: 'Payroll & HR',
        subtitle: 'Employee salaries, tax deductions, and payslips',
        breadcrumbs: [
            { label: 'Modules' },
            { label: 'Payroll & HR' },
        ],
    },
    '/time-tracking': {
        title: 'Time Tracking',
        subtitle: 'Log billable hours and timesheets',
        breadcrumbs: [
            { label: 'Modules' },
            { label: 'Time Tracking' },
        ],
    },
    '/crm': {
        title: 'CRM',
        subtitle: 'Customer relationships and pipeline',
        breadcrumbs: [
            { label: 'Modules' },
            { label: 'CRM' },
        ],
    },

    // ═══════════════════════════════════════════════════════════
    //  TOOLS & PRODUCTIVITY
    // ═══════════════════════════════════════════════════════════
    '/receipt-scanner': {
        title: 'Receipt Scanner',
        subtitle: 'Scan and auto-categorise receipts',
        breadcrumbs: [
            { label: 'Tools' },
            { label: 'Receipt Scanner' },
        ],
    },
    '/tasks': {
        title: 'Tasks',
        subtitle: 'Track team tasks and deadlines',
        breadcrumbs: [
            { label: 'Tools' },
            { label: 'Tasks' },
        ],
    },
    '/ai-tools': {
        title: 'AI Assistant',
        subtitle: 'Smart categorisation, receipt OCR, and insights',
        breadcrumbs: [
            { label: 'Tools' },
            { label: 'AI Assistant' },
        ],
    },

    // ─── Client Access ───────────────────────────────────────
    '/portal': {
        title: 'Customer / Vendor Portal',
        subtitle: 'Client self-service portal',
        breadcrumbs: [
            { label: 'Client Access' },
            { label: 'Portal' },
        ],
    },

    // ═══════════════════════════════════════════════════════════
    //  SETTINGS
    // ═══════════════════════════════════════════════════════════
    '/settings': {
        title: 'Settings',
        subtitle: 'Organisation profile, tax, invoicing & preferences',
        breadcrumbs: [{ label: 'Settings' }],
    },
    '/collaboration': {
        title: 'Access',
        subtitle: 'Who can open this business: your staff, from Payroll, and outside people',
        breadcrumbs: [
            { label: 'Settings', href: '/settings' },
            { label: 'Access' },
        ],
    },
    '/email': {
        title: 'Email & Notifications',
        subtitle: 'Email integration and notification preferences',
        breadcrumbs: [
            { label: 'Settings', href: '/settings' },
            { label: 'Email & Notifications' },
        ],
    },
    '/ai-settings': {
        title: 'AI Settings',
        subtitle: 'API keys, usage, and AI preferences',
        breadcrumbs: [
            { label: 'Settings', href: '/settings' },
            { label: 'AI Settings' },
        ],
    },

    // ─── Integrations ────────────────────────────────────────
    '/integrations': {
        title: 'Integrations',
        subtitle: 'Everything your business connects, and how each one reaches Karr',
        breadcrumbs: [
            { label: 'Integrations' },
        ],
    },
    '/import': {
        title: 'Import Data',
        subtitle: 'Migrate from Tally, Zoho, or spreadsheets',
        breadcrumbs: [
            { label: 'Integrations', href: '/integrations' },
            { label: 'Import Data' },
        ],
    },
    '/import/tally': {
        title: 'Import from Tally',
        subtitle: "One company, read from Tally's own XML, tied to the paise",
        breadcrumbs: [
            { label: 'Import Data', href: '/import' },
            { label: 'From Tally' },
        ],
    },
    '/import/vyapar': {
        title: 'Import from Vyapar',
        subtitle: 'The whole Vyapar books, from the phone app or the desktop app, tied to the paise',
        breadcrumbs: [
            { label: 'Import Data', href: '/import' },
            { label: 'From Vyapar' },
        ],
    },
    '/import/vouchers': {
        title: 'Import Vouchers',
        subtitle: 'Now part of Import from Tally',
        breadcrumbs: [
            { label: 'Import Data', href: '/import' },
            { label: 'Vouchers' },
        ],
    },
    '/developers': {
        title: 'Developers',
        subtitle: 'Keys, webhooks, the documents door and the API reference',
        breadcrumbs: [
            { label: 'Integrations', href: '/integrations' },
            { label: 'Developers' },
        ],
    },
    // connect-2: both forward to /developers; kept so an old link still has a name.
    '/api-settings': {
        title: 'Developers',
        subtitle: 'Now under Developers',
        breadcrumbs: [
            { label: 'Developers' },
        ],
    },
    '/api-docs': {
        title: 'API reference',
        subtitle: 'Now under Developers, Docs',
        breadcrumbs: [
            { label: 'Developers', href: '/developers' },
            { label: 'Docs' },
        ],
    },

    // ═══════════════════════════════════════════════════════════
    //  PRACTICE (CA/Accountant firms)
    // ═══════════════════════════════════════════════════════════
    '/portfolio': {
        title: 'Board',
        subtitle: 'Every entity you steer, ranked by who needs you now',
        breadcrumbs: [{ label: 'Board' }],
        // cc-10: Practice gets undivided attention — the header pill + the
        // page's own tabs carry the identity; no breadcrumb/title band.
        noHeader: true,
    },
    '/practice-crm': {
        noHeader: true, // cc-10: the PracticeTabs strip carries the identity
        title: 'CRM',
        subtitle: 'Who your clients are, how to reach them, what they pay you',
        breadcrumbs: [{ label: 'CRM' }],
    },
    '/requests': {
        noHeader: true, // cc-10: the PracticeTabs strip carries the identity
        title: 'Client Requests',
        subtitle: 'Documents asked for and provided',
        breadcrumbs: [{ label: 'CRM' }, { label: 'Client Requests' }],
    },
    '/partner': {
        noHeader: true, // cc-10: the PracticeTabs strip carries the identity
        title: 'Partner Program',
        subtitle: 'Refer businesses to Karr and earn 15% when they pay',
        breadcrumbs: [{ label: 'Practice' }, { label: 'Partner Program' }],
    },
    '/notifications': {
        title: 'Notifications',
        subtitle: 'Everything that needs your attention',
        breadcrumbs: [{ label: 'Notifications' }],
    },
    '/gst-tds': {
        title: 'GST & TDS',
        subtitle: 'Live ledgers, GSTR-2B, return status & TDS — powered by Sandbox',
        breadcrumbs: [{ label: 'Tax & Compliance' }, { label: 'GST & TDS' }],
    },
    '/softex': {
        title: 'Software Exports',
        subtitle: 'Declare your software and IT exports to RBI through STPI',
        breadcrumbs: [{ label: 'Tax & Compliance' }, { label: 'Software Exports' }],
    },
    '/gst-filing': {
        title: 'GST Filing Cockpit',
        subtitle: 'One click to a perfect return — evidence-gated GSTR-1 / GSTR-3B',
        breadcrumbs: [{ label: 'Tax & Compliance' }, { label: 'GST Filing Cockpit' }],
    },
    '/tds-filing': {
        title: 'TDS Filing Cockpit',
        subtitle: 'What you withheld, when it is due, and whether the government agrees it was paid',
        breadcrumbs: [{ label: 'Tax & Compliance' }, { label: 'TDS Filing Cockpit' }],
    },
    '/payroll-filing': {
        title: 'Payroll Filing Cockpit',
        subtitle: 'Provident fund, ESI, professional tax and the welfare fund — the whole year, and where each month stands',
        breadcrumbs: [{ label: 'Tax & Compliance' }, { label: 'Payroll Filing Cockpit' }],
    },
    '/itr-filing': {
        title: 'ITR Filing Cockpit',
        subtitle: 'Compute the income-tax return off audited books, catch every mistake',
        breadcrumbs: [{ label: 'Tax & Compliance' }, { label: 'ITR Filing Cockpit' }],
    },
    '/audit-trail': {
        title: 'Audit Trail',
        subtitle: 'Every change to your financial records',
        breadcrumbs: [{ label: 'Audit Trail' }],
    },
    // auditflow: one Audit screen. Closing the year, the engagement desk and
    // the 3CD used to be three pages with three titles; they are chapters of
    // this one, and `/audit-desk` and `/tax-audit` redirect here.
    '/audit': {
        title: 'Audit',
        subtitle: 'Which audit this business owes this year, where it stands, and the one next thing',
        breadcrumbs: [{ label: 'Audit' }],
        // The identity card directly underneath names this client, this year
        // and the audit it is on, so a tall serif title would say the same
        // thing again and charge a third of the fold for it. The year picker
        // and the chapter pills live in this row (`HeaderSlot`).
        dense: true,
    },
    '/my-audits': {
        title: 'Every business I audit',
        subtitle: 'Every engagement you audit: what is behind, and which appointments are running out',
        breadcrumbs: [{ label: 'Every business I audit' }],
    },
    '/period-locks': {
        title: 'Close & Lock Periods',
        subtitle: 'Roll the year into retained earnings and lock closed months against backdating',
        breadcrumbs: [{ label: 'Close & Lock Periods' }],
    },
    '/practice': {
        title: 'Practice Dashboard',
        subtitle: 'Multi-client overview for consultants',
        breadcrumbs: [
            { label: 'Practice' },
            { label: 'Dashboard' },
        ],
    },
    '/create-practice': {
        title: 'Create Practice',
        breadcrumbs: [{ label: 'Create Practice' }],
        noHeader: true,
    },
};

/**
 * Routes whose page maps to an accounting term in experienceLevel.ts's TERM_MAP.
 * In Simple (business_owner) mode the layout header shows the plain-language term
 * instead of the jargon title — e.g. /credit-notes → "Customer refund",
 * /journals → "Manual adjustment". This is what wires `translateTerm` (previously
 * zero call sites) into the actual UI. Only business_owner titles change; Standard
 * and Expert keep the descriptive registry title.
 */
export const ROUTE_TERM_KEY: Record<string, string> = {
    '/credit-notes': 'credit_note',
    '/debit-notes': 'debit_note',
    '/estimates': 'estimate',
    '/purchase-orders': 'purchase_order',
    '/delivery-challans': 'delivery_challan',
    '/recurring-invoices': 'recurring_invoice',
    '/journals': 'journal_entry',
    '/accounts': 'chart_of_accounts',
    '/bank-reconciliation': 'bank_reconciliation',
    '/receivables': 'receivables_payables',
    '/budgeting': 'budgeting',
    '/multi-currency': 'multi_currency',
    '/consolidation': 'consolidation',
    '/fixed-assets': 'fixed_asset',
    '/gst-filing': 'gst_return',
    '/payment-links': 'payment_links',
};

/**
 * Get route metadata. Returns undefined for unknown routes.
 */
export function getRouteMeta(path: string): RouteMeta | undefined {
    return ROUTE_REGISTRY[path];
}

/**
 * Get all registered routes (for debugging/admin).
 */
export function getAllRoutes(): Record<string, RouteMeta> {
    return { ...ROUTE_REGISTRY };
}


